import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import getRealTimeWarehouseInventory from "@wausauCustomBlueprint/Store/Data/RealtimeInventory/Handlers/GetRealTimeWarehouseInventory";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    widgetId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
}

interface ExtendedWareHouseDto extends WarehouseDto {
    properties: { [key: string]: string };
}

const mapStateToProps = (state: ApplicationState) => {
    const productSettings = getSettingsCollection(state).productSettings;
    // TODO: userName is being used here to determine whether the user is logged in or not. Login status can be checked more directly.
    const userName = state.context.session.userName;
    const showInventory = state.context.showInventory;
    const language = state.context.session.language;
    const page = getCurrentPage(state);
    const { productList, productDetails, myListDetails } = state.pages;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        productSettings,
        userName,
        showInventory,
        language,
        page,
        productList,
        productDetails,
        myListDetails,
        cartState,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    noSupplyModelAvailableMessageText?: TypographyPresentationProps;
    disclaimerMessageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    estimatedDeliveryDateTableCell?: DataTableCellBaseProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            padding: 10px 0;
        `,
    },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 540,
        cssOverrides: {
            modalContent: css`
                padding: 20px;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    noSupplyModelAvailableMessageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    disclaimerMessageText: {
        css: css`
            margin-top: 20px;
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    availabilityByWarehouseLink: {
        color: "#2E64B0",
        css: css`
            text-decoration: underline;
        `,
        typographyProps: {
            css: css`
                font-size: large;
            `,
        },
    },
    estimatedDeliveryDateTableCell: {
        css: css`
            text-align: center;
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 5px;
    border-radius: 5px;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    ${IconWrapper} {
        margin-right: 10px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability: React.FC<Props> = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    userName,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    language,
    productList,
    productDetails,
    myListDetails,
    cartState,
    page,
    extendedStyles,
}) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);

        const { productQty, unitOfMeasure } = getProductQuantityFromPage(page.name);
        getRealTimeWarehouseInventory({
            productId,
            qtyOrdered: productQty,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    // ~~~ Todo: Refactor if possible.
    const getProductQuantityFromPage = (pageName: string) => {
        let productQty = 1;
        let unitOfMeasure = "EA";
        const sanitizedPageName = getSanitizedPageName(pageName);

        if (sanitizedPageName === "productdetails") {
            if (productDetails?.productInfosById && productDetails?.productInfosById[productId]) {
                const productInfo = productDetails?.productInfosById[productId];

                if (productInfo) {
                    productQty = productInfo.qtyOrdered;
                    unitOfMeasure = productInfo.unitOfMeasure;
                }
            }
        } else if (sanitizedPageName === "mylistsdetails") {
            if (myListDetails?.productInfosByWishListLineId) {
                const productInfo = Object.values(myListDetails.productInfosByWishListLineId).find(
                    o => o?.productId === productId,
                );
                if (productInfo) {
                    productQty = productInfo.qtyOrdered;
                    unitOfMeasure = productInfo.unitOfMeasure;
                }
            }
        } else if (sanitizedPageName === "productlist") {
            if (productList?.productInfosByProductId) {
                const productInfo = Object.values(productList?.productInfosByProductId).find(
                    o => o?.productId === productId,
                );
                if (productInfo) {
                    productQty = productInfo.qtyOrdered;
                    unitOfMeasure = productInfo.unitOfMeasure;
                }
            }
        } else if (sanitizedPageName === "cart") {
            if (cartState?.value?.cartLines && cartState?.value?.cartLines?.length > 0) {
                const cartLine = cartState?.value?.cartLines?.find(o => o.productId === productId);
                if (cartLine) {
                    productQty = cartLine.qtyOrdered || 1;
                    unitOfMeasure = cartLine.unitOfMeasure;
                }
            }
        }
        return { productQty, unitOfMeasure };
    };

    const getSanitizedPageName = (pageName: string) => {
        return pageName.split(" ").join("").toLowerCase();
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon color={color} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    const convertStringToDate = (dateString: string) => {
        try {
            if (typeof dateString !== "string") {
                throw new Error("Input must be a string in the format YYYYMMDD.");
            }

            if (dateString.length !== 8) {
                throw new Error("Input must be exactly 8 characters long in the format YYYYMMDD.");
            }

            const year = Number(dateString.slice(0, 4));
            const month = Number(dateString.slice(4, 6)) - 1;
            const day = Number(dateString.slice(6, 8));

            if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
                throw new Error("Input must contain only numeric values in the format YYYYMMDD.");
            }

            const date = new Date(year, month, day);

            if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
                throw new Error("Invalid date. Please provide a valid date in the format YYYYMMDD.");
            }

            return date;
        } catch (error) {
            console.error("Error in convertStringToDate:", error.message);
        }
    };
    const showInventoryMessage = userName !== null && userName !== "";

    return (
        <GridContainer {...styles.container}>
            {showInventoryMessage && <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>}
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <>
                                        {!warehouses || warehouses.length === 0 ? (
                                            <InventoryMessage color="danger" {...styles.inventoryMessage}>
                                                <Icon color="danger" src={X} />
                                                <Typography
                                                    {...styles.noSupplyModelAvailableMessageText}
                                                    data-test-selector="noSupplyModelAvailableMessage"
                                                >
                                                    {siteMessage("AvailabilityModel_NoSupplModelAvailableMessage")}
                                                </Typography>
                                            </InventoryMessage>
                                        ) : (
                                            <DataTable
                                                {...styles.availabilityByWarehouseTable}
                                                data-test-selector="availabilityByWarehouseModal"
                                            >
                                                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Warehouse")}
                                                    </DataTableHeader>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("QTY")}
                                                    </DataTableHeader>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Estimated Delivery Date")}
                                                    </DataTableHeader>
                                                </DataTableHead>
                                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                    {warehouses?.map((warehouse, index) => {
                                                        const extendedWarehouse = warehouse as ExtendedWareHouseDto;
                                                        const confirmedDeliveryDate = getLocalizedDateTime({
                                                            dateTime: new Date(
                                                                convertStringToDate(
                                                                    extendedWarehouse.properties?.confirmedDeliveryDate,
                                                                )!,
                                                            ),
                                                            language,
                                                            options: {
                                                                year: "numeric",
                                                                month: "numeric",
                                                                day: "numeric",
                                                            },
                                                        });
                                                        return (
                                                            <DataTableRow
                                                                {...styles.availabilityByWarehouseTableRow}
                                                                key={warehouse.name + index}
                                                            >
                                                                <DataTableCell
                                                                    {...styles.availabilityByWarehouseTableCell}
                                                                    data-test-selector="warehouseAvailabilityModal_Description"
                                                                >
                                                                    {warehouse.description || warehouse.name}
                                                                </DataTableCell>
                                                                <DataTableCell
                                                                    {...styles.availabilityByWarehouseTableCell}
                                                                    data-test-selector="warehouseAvailabilityModal_Quantity"
                                                                >
                                                                    {warehouse.qty}
                                                                </DataTableCell>

                                                                <DataTableCell
                                                                    {...styles.estimatedDeliveryDateTableCell}
                                                                    data-test-selector="warehouseAvailabilityModal_Estimated_Delivery_Date"
                                                                >
                                                                    {confirmedDeliveryDate}
                                                                </DataTableCell>
                                                            </DataTableRow>
                                                        );
                                                    })}
                                                </DataTableBody>
                                            </DataTable>
                                        )}
                                        <StyledWrapper {...styles.disclaimerMessageText}>
                                            <Typography data-test-selector="disclaimerMessage">
                                                {siteMessage("AvailabilityModel_DisclaimerMessage")}
                                            </Typography>
                                        </StyledWrapper>
                                    </>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
